import { useState } from 'react'
import { present } from '../../../lib/util/present'
import { PaymentPlanFieldsModel } from './paymentPlanFields'

export type MoreOptionsDisclosureRow = PaymentPlanFieldsModel & {
  paid_with_hra?: boolean | null
  is_prepayment_agreement?: boolean | null
}

/**
 * Shared "More options" disclosure state for the new- and edit-expense forms. Reveals the
 * section up front when the row already carries a payment plan / recurring medication / HRA
 * choice / prepayment agreement / non-default shareability, so editing or scanning such an
 * expense doesn't hide the relevant control. Recomputed on every render (not just mount)
 * because Textract or an OCR prefill can complete asynchronously after mount.
 *
 * An unresolved ('unknown') shareability forces the section open even over a manual close,
 * since it's the one condition here that's a genuinely blocking, must-fix validation state —
 * the other conditions are already-set, non-blocking values a member should still be able to
 * collapse away (e.g. after reviewing an existing payment plan).
 */
export function useMoreOptionsDisclosure(row: MoreOptionsDisclosureRow) {
  const moreOptionsDefaultOpen = !!row.is_payment_plan || !!row.is_recurring_medication ||
    present(row.paid_with_hra) || !!row.is_prepayment_agreement ||
    (present(row.shareability) && row.shareability !== 'shareable')
  const hasUnresolvedShareability = row.shareability === 'unknown'

  const [moreOptionsOverride, setMoreOptionsOverride] = useState<boolean | undefined>(undefined)
  const moreOptionsOpen = hasUnresolvedShareability || (moreOptionsOverride ?? moreOptionsDefaultOpen)

  return { moreOptionsOpen, setMoreOptionsOverride }
}
